import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";

export const CUSTOMER_SEARCH_RESOURCE = "customer" as const;
export const CUSTOMER_SEARCH_OPERATION = "search" as const;
export const CUSTOMER_SEARCH_API_PATH = `/v1/${CUSTOMER_SEARCH_RESOURCE}/${CUSTOMER_SEARCH_OPERATION}` as const;

export type CustomerSearchParameters = paths[typeof CUSTOMER_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type CustomerSearchBody =
  paths[typeof CUSTOMER_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type CustomerSearchResponse =
  paths[typeof CUSTOMER_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

export function useCustomerSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<CustomerSearchParameters, CustomerSearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<CustomerSearchResponse, Error, CustomerSearchResponse, readonly unknown[]>;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<PaginatedOptions<CustomerSearchParameters, CustomerSearchBody>>(options);

  const requestParameters = {
    ...(optionsWithDefaults.parameters || {}),
    ...handlePaginatedParameters(optionsWithDefaults.parameters),
  };

  return useQuery({
    queryKey: [
      CUSTOMER_SEARCH_RESOURCE,
      CUSTOMER_SEARCH_OPERATION,
      { parameters: requestParameters, body: optionsWithDefaults.body },
    ],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<CustomerSearchResponse>(
        CUSTOMER_SEARCH_API_PATH,
        optionsWithDefaults.body,
        {
          params: requestParameters,
        },
      );

      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
